import popupStyle from 'ol-ext/overlay/Popup.css';
import query from '@giswebgroup/ki-wcp-base/src/decorators/query';
import { fromLonLat } from 'ol/proj';
import { html, LitElement } from 'lit-element';
import { Map, View } from 'ol';
import LoaderMixin from '@giswebgroup/ki-wcp-base/src/common/LoaderMixin';
import olcss from 'ol/ol.css';
import autoResize from '@giswebgroup/ki-wcp-base/src/decorators/autoResize';
import '../ki-icon-switcher/ki-icon-switcher';
import ovStyle from 'ol-ext/control/Overview.css';
import i18n from '@giswebgroup/ki-wcp-base/src/decorators/i18n';
import Popup from 'ol-ext/overlay/Popup';
import { debounce } from 'lodash-es/function';
import computed from '@giswebgroup/ki-wcp-base/src/decorators/computed';
import nls from '../../locales/index.nls';
import style from './ki-station-map.css';
import responsive from '@giswebgroup/ki-wcp-base/src/decorators/responsive';
import {Attribution, defaults as defaultControls} from 'ol/control';
import {setWithExpiry, getWithExpiry} from "@giswebgroup/ki-wcp-base/src/common/util";

export default
@i18n(nls)
@responsive
@autoResize()
class KiStationMap extends LoaderMixin(LitElement) {
  static styles = style;

  static get properties() {
    return {
      view: { type: Object },
      persistancetimeout: {type: Number },
      hideAttributions: { type: Boolean},
      selectedStations: { type: Array },
    };
  }

  constructor() {
    super();
    this.disableMouseOver = false;
    this.persistancetimeout = 3600;
    this.hideAttributions = false;
    this.view = {
      projection: 'EPSG:3857',
      minZoom: 7,
      zoom: 8,
    };
  }

  
  firstUpdated() {
    this._createMap();
  }
  @computed('view')
  get _view() {
    return new View(this.view);
  }

  @query('#map')
  __mapElement;

  @query('#scale-info')
  __infoElement;

  _createMap() {
    this.map = new Map({
      target: this.__mapElement,
      view: this._view,
      controls: this.hideAttributions ? [] : defaultControls({zoom: false, attribution: false}).extend([new Attribution({
        collapsible: false,
      })]),
    });

    const readyEvent = new CustomEvent('kistationmapready');
    this.setMapEvents()
    
    this.map.once('rendercomplete', () => {
      this.dispatchEvent(readyEvent);
    })

    this.map.on('precompose', function(evt) {
      if(evt.context){
      evt.context.imageSmoothingEnabled = false;
      evt.context.webkitImageSmoothingEnabled = false;
      evt.context.mozImageSmoothingEnabled = false;
      evt.context.msImageSmoothingEnabled = false;
      }
    });
    this.map.once('postrender', () => {

      this.retry = setInterval(()=>{
        if(!this.map.isRendered()){
          this.resize();
        }else{
          clearInterval(this.retry);
   
        }

      },1000)


    });
    this.map.once('postrender', () => {

      const extent = this.persistancetimeout ? getWithExpiry("ki-station-map-zoom") : null;
      if(extent){
        this.map.getView().fit(extent, this.map.getSize());
      }
    });
  }

  setMapEvents(){
    this._initPopup();

    const popupelement = document.createElement('div');
    popupelement.style.padding = '10px';
    let selected = null;
    let tooltip = null;
    if(this.persistancetimeout){
      this.map.on('moveend', evt =>{
        const map = evt.map;
        const extent = map.getView().calculateExtent(map.getSize());
        setWithExpiry("ki-station-map-zoom", extent, this.persistancetimeout*1000);
      });
    }


    this.map.on('singleclick', evt => {
      this.map.forEachFeatureAtPixel(
        evt.pixel,
        feature => {
          if (feature.get('url')) {
            window.open(feature.get('url'), '_blank');
            return true;
          }
          return false;
        },
        { layerFilter: layer => layer.get('haslinks') },
      );
    });
    const event = this.map.on('pointermove', evt => {
      this.map.getTargetElement().style.cursor = '';
      if (this.disableMouseOver) {
        if (tooltip !== null) {
          this._popup.hide();
          tooltip = null;
        }
        return false;
      }
      if (selected !== null) {
        selected.setStyle(selected._style);
        selected = null;
      }
      if (tooltip !== null) {
        this._popup.hide();
        tooltip = null;
      }
      this.map.forEachFeatureAtPixel(
        evt.pixel,
        feature => {
          if (feature.get('url')) {
            this.map.getTargetElement().style.cursor = 'pointer';
          }

          if (feature._toolTip) {
            popupelement.innerHTML = feature._toolTip;
            this._popup.show(this.map.getCoordinateFromPixel(evt.pixel), popupelement);
            tooltip = true;
          }
          if (feature._hoverStyle) {
            selected = feature;
            feature.setStyle(feature._hoverStyle);
            return true;
          }
          return false;
        },
        { layerFilter: layer => layer.get('haslinks') || layer.get('interactive') },
      );
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.retry && clearInterval(this.retry)
  }

  updated(changedProperties) {
    super.update(changedProperties);
    if (this.map) {
      // TODO bug ? view
      this.map.setProperties(changedProperties);
      // TODO
      if (changedProperties.has('view')) {
        this.map.setView(this._view);
      }
    }
  }

  render() {
    // language=html
    this.disableMouseOver = this.classList.contains('sm-screen');
    return html`
      <link rel="stylesheet" href="${olcss}" />
      <link rel="stylesheet" href="${ovStyle}" />
      <link rel="stylesheet" href="${popupStyle}" />
      <div id="map"></div>
      ${this._renderLoader()}
    `;
  }

  gotoLocation() {
    // TODO timeout
    // need https
    const p = new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        pos => {
          this.map.getView().animate({
            center: fromLonLat([pos.coords.longitude, pos.coords.latitude], this.map.getView().getProjection()),
            duration: 500,
          });
          resolve();
        },
        e => {
          console.error(e);
          reject(e);
        },
        {
          timeout: 2000,
        },
      );
    });
    // TODO
    this.promiseLoader(p);
  }

  resize() {
    if (this.map) {
      this.__mapElement.style.width = 'auto';
      this.__mapElement.style.transform = '';
      this.map.updateSize();
    }
  }

  // move to ki-map-popup
  _initPopup() {
    const popup = new Popup({
      closeBox: false,
    });
    this._popup = popup;
    this.map.addOverlay(popup);

    const showPopup = (e) => {
      if(!this.map.disablePopup){
        const { coordinate, element } = e.detail;
        popup.show(coordinate, element);
      }

    };

    this.map.on('show-popup', showPopup);
    this.map.on('close-popup', function () {
      popup.hide();
    });
    // show pointer
 
  }
}

customElements.define('ki-station-map', KiStationMap);
